import React, { useState } from "react";
import { UserAddOutlined } from "@ant-design/icons";
import { Button, Card, Input, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { AddCustomerModal, type CreatedCustomer } from "./AddCustomerModal";
import { CustomerExpandedRow } from "./CustomerExpandedRow";
import { ProductSequenceFilter } from "./ProductSequenceFilter";
import { useProductsCustomers, type CustomerListItem } from "./useProductsCustomers";

const sourceColor: Record<string, string> = {
  cart: "geekblue",
  woocommerce_import: "purple",
  app_import: "cyan",
  migration: "orange",
};

export const ProductsCustomers: React.FC = () => {
  const { t } = useTranslation(["products", "common"]);
  const {
    data,
    isLoading,
    isFetching,
    page,
    pageSize,
    searchTerm,
    productSequence,
    firstPurchaseFrom,
    firstPurchaseTo,
    sortBy,
    sortDir,
    setPage,
    setSearchTerm,
    setProductSequence,
    setFirstPurchaseRange,
    setSort,
    resetFilters,
  } = useProductsCustomers();

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);

  const handleCustomerCreated = (customer: CreatedCustomer) => {
    void queryClient.invalidateQueries({ queryKey: ["productsCustomers"] });
    navigate(`/products/customers/${customer.id}`);
  };

  const hasActiveFilters =
    Boolean(searchTerm) || productSequence.length > 0 || Boolean(firstPurchaseFrom) || Boolean(firstPurchaseTo);

  return (
    <Card>
      <Space direction="vertical" style={{ width: "100%" }} size={24}>
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Space wrap align="center" style={{ width: "100%", justifyContent: "space-between" }}>
            <Space wrap align="center">
              <Input.Search
                placeholder={t<string>("customersTab.searchPlaceholder")}
                defaultValue={searchTerm}
                onSearch={(value) => setSearchTerm(value.trim())}
                allowClear
                style={{ width: 320 }}
              />

              {hasActiveFilters ? (
                <Button onClick={resetFilters} type="link">
                  {t("search.reset")}
                </Button>
              ) : null}
            </Space>
            <Button type="primary" icon={<UserAddOutlined />} onClick={() => setAddCustomerOpen(true)}>
              Dodaj klienta
            </Button>
          </Space>

          <ProductSequenceFilter
            value={productSequence}
            onChange={setProductSequence}
            firstPurchaseFrom={firstPurchaseFrom}
            firstPurchaseTo={firstPurchaseTo}
            onFirstPurchaseRangeChange={setFirstPurchaseRange}
          />
        </Space>

        <Table<CustomerListItem>
          rowKey="id"
          dataSource={data?.list ?? []}
          scroll={{ x: true }}
          loading={isLoading || isFetching}
          onChange={(pagination, _filters, sorter) => {
            const single = Array.isArray(sorter) ? sorter[0] : sorter;
            const columnKey = single?.columnKey;
            const order = single?.order;
            const nextSortBy: "totalRevenue" | "createdAt" | null =
              order && (columnKey === "totalRevenue" || columnKey === "createdAt")
                ? (columnKey)
                : null;
            const nextSortDir: "asc" | "desc" | null = order ? (order === "ascend" ? "asc" : "desc") : null;

            if (nextSortBy !== sortBy || nextSortDir !== sortDir) {
              setSort(nextSortBy, nextSortDir);
              return;
            }

            if (pagination.current && pagination.current !== page) {
              setPage(pagination.current);
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.count ?? 0,
            pageSizeOptions: [String(pageSize)],
            showSizeChanger: false,
          }}
          expandable={{
            expandedRowRender: (customer) => <CustomerExpandedRow customer={customer} />,
          }}
        >
          <Table.Column<CustomerListItem>
            title="#"
            width={50}
            key="index"
            render={(_, __, i) => (page > 1 ? (page - 1) * pageSize + i + 1 : i + 1)}
          />
          <Table.Column<CustomerListItem>
            title={t("customersTab.tableHeader.name")}
            key="name"
            render={(_, row) => {
              const name = `${row.lastName ?? ""} ${row.firstName ?? ""}`.trim();
              return name || "-";
            }}
          />
          <Table.Column<CustomerListItem>
            title={t("customersTab.tableHeader.email")}
            dataIndex="email"
            key="email"
            render={(email: string | null) => email ?? "-"}
          />
          <Table.Column<CustomerListItem>
            title={t("customersTab.tableHeader.source")}
            dataIndex="source"
            key="source"
            render={(source: string | null) =>
              source ? (
                <Tag color={sourceColor[source] ?? "default"}>
                  {t(`customersTab.source.${source}`, { defaultValue: source })}
                </Tag>
              ) : (
                "-"
              )
            }
          />
          <Table.Column<CustomerListItem>
            title={t("customersTab.tableHeader.bundlesCount")}
            dataIndex="bundlesCount"
            key="bundlesCount"
            render={(count: number) => (count > 0 ? <Tag color="blue">{count}</Tag> : <Tag>0</Tag>)}
          />
          <Table.Column<CustomerListItem>
            title={t("customersTab.tableHeader.totalRevenue")}
            dataIndex="totalRevenue"
            key="totalRevenue"
            sorter
            sortOrder={sortBy === "totalRevenue" ? (sortDir === "asc" ? "ascend" : "descend") : null}
            render={(value: number) =>
              value ? <b>{(value / 100).toFixed(2)} PLN</b> : <Typography.Text type="secondary">-</Typography.Text>
            }
          />
          <Table.Column<CustomerListItem>
            title={t("customersTab.tableHeader.createdAt")}
            dataIndex="createdAt"
            key="createdAt"
            sorter
            sortOrder={sortBy === "createdAt" ? (sortDir === "asc" ? "ascend" : "descend") : null}
            render={(createdAt: string | null) => (createdAt ? dayjs(createdAt).format("DD.MM.YYYY HH:mm") : "-")}
          />
          <Table.Column<CustomerListItem>
            title={t("customersTab.tableHeader.stripeId")}
            dataIndex="stripeCustomerId"
            key="stripeCustomerId"
            render={(stripeId: string | null) =>
              stripeId ? (
                <Typography.Text copyable={{ text: stripeId }} style={{ fontSize: 12 }}>
                  {stripeId.slice(0, 12)}…
                </Typography.Text>
              ) : (
                "-"
              )
            }
          />
          <Table.Column<CustomerListItem>
            title=""
            key="details"
            render={(_, row) => <Link to={`/products/customers/${row.id}`}>{t("common:details")}</Link>}
          />
        </Table>
      </Space>

      <AddCustomerModal
        open={addCustomerOpen}
        onClose={() => setAddCustomerOpen(false)}
        onCreated={handleCustomerCreated}
      />
    </Card>
  );
};
